// Small parsing helpers shared by the Competencia form normalizers.
import { isIsoDay } from "@/components/market-intel/dates";

/** Result of a form normalizer: the payload, or the i18n key of the error. */
export type FormResult<T> =
  { ok: true; data: T } | { ok: false; error: string };

export const optionalText = (value: string | null | undefined): string | null =>
  value?.trim() || null;

/** "example.com" -> "https://example.com"; anything else is kept trimmed. */
export const optionalUrl = (
  value: string | null | undefined,
): string | null => {
  const url = value?.trim();
  if (!url) return null;
  return /^[a-z][a-z0-9+.-]*:/i.test(url) ? url : `https://${url}`;
};

/**
 * A non-negative number typed by a person: "1.234" / "1 234" (thousands) and
 * "2,5" (Spanish decimal comma) are understood. "" -> null, garbage -> NaN.
 */
export const parseAmount = (value: string, integer = false): number | null => {
  const raw = value.trim().replace(/[\s\u00a0%\u20ac]/g, "");
  if (!raw) return null;
  let normalized = raw;
  if (/^\d{1,3}(\.\d{3})+$/.test(raw)) normalized = raw.replace(/\./g, "");
  else if (/^\d+(,\d+)?$/.test(raw)) normalized = raw.replace(",", ".");
  else if (/^\d{1,3}(\.\d{3})+,\d+$/.test(raw))
    normalized = raw.replace(/\./g, "").replace(",", ".");
  if (!/^\d+(\.\d+)?$/.test(normalized)) return Number.NaN;
  const amount = Number(normalized);
  if (integer && !Number.isInteger(amount)) return Number.NaN;
  return amount;
};

export const isInvalidAmount = (value: number | null): boolean =>
  value !== null && (!Number.isFinite(value) || value < 0);

/** "" -> null; a valid YYYY-MM-DD is kept; anything else is invalid. */
export const parseOptionalDay = (
  value: string,
): { ok: true; day: string | null } | { ok: false } => {
  const day = value.trim();
  if (!day) return { ok: true, day: null };
  return isIsoDay(day) ? { ok: true, day } : { ok: false };
};

/** Postgres unique violation, as surfaced by PostgREST / FakeRest. */
export const isDuplicateError = (error: unknown): boolean =>
  /23505|duplicate|unique/i.test(
    error instanceof Error ? error.message : String(error ?? ""),
  );

/** Text for a form input from a nullable number ("2.5" -> "2,5" in Spanish). */
export const amountInput = (
  value: number | null | undefined,
  locale: string,
): string =>
  value == null
    ? ""
    : locale.startsWith("en")
      ? String(value)
      : String(value).replace(".", ",");

/** Accent- and case-insensitive text for search. */
export const foldText = (value: string | null | undefined): string =>
  (value ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
